import axios from 'axios';
import { Platform, StatusCode } from 'core/common/entities';
import { ApiError } from 'core/common/errors';
import { stringifyQueryParams } from 'core/common/utils/getQueryParams';
import {
  AxiosInstance,
  HttpRequestConfig,
  isAxiosError,
  RawAxiosRequestHeaders,
  ResponseHeaders,
} from './entities';
import { HttpClient } from './interfaces';

export class AxiosHttpClient implements HttpClient {
  private client?: AxiosInstance;

  constructor(
    private readonly baseUrl: string,
    private readonly config?: HttpRequestConfig,
  ) {}

  private getClient(): AxiosInstance {
    if (!this.client) {
      this.client = axios.create({
        headers: {
          Platform: Platform.WEB,
          Source: 'appnebula',
        },
        paramsSerializer: (params) => {
          return stringifyQueryParams(params);
        },
        ...(this.config || {}),
      });
    }

    return this.client;
  }

  async get<ResponseData = unknown, ResponseMeta = unknown>(
    url: string,
    config: HttpRequestConfig = {},
  ) {
    return this.request<ResponseData, ResponseMeta>({
      url,
      method: 'GET',
      ...config,
    });
  }

  async post<ResponseData = unknown, ResponseMeta = unknown>(
    url: string,
    config: HttpRequestConfig = {},
  ) {
    return this.request<ResponseData, ResponseMeta>({
      url,
      method: 'POST',
      ...config,
    });
  }

  async put<ResponseData = unknown, ResponseMeta = unknown>(
    url: string,
    config: HttpRequestConfig = {},
  ) {
    return this.request<ResponseData, ResponseMeta>({
      url,
      method: 'PUT',
      ...config,
    });
  }

  async patch<ResponseData = unknown, ResponseMeta = unknown>(
    url: string,
    config: HttpRequestConfig = {},
  ) {
    return this.request<ResponseData, ResponseMeta>({
      url,
      method: 'PATCH',
      ...config,
    });
  }

  async delete<ResponseData = unknown, ResponseMeta = unknown>(
    url: string,
    config: HttpRequestConfig = {},
  ) {
    return this.request<ResponseData, ResponseMeta>({
      url,
      method: 'DELETE',
      ...config,
    });
  }

  private async request<ResponseData = unknown, ResponseMeta = unknown>(
    config: HttpRequestConfig,
  ): Promise<{ data: ResponseData; meta: ResponseMeta; success: boolean }> {
    try {
      const client = this.getClient();

      const isFormData = config.data instanceof FormData;

      const { data: result } = await client.request({
        withCredentials: true,
        ...config,
        baseURL: this.baseUrl,
        method: config.method,
        headers: {
          ...(isFormData ? {} : { 'Content-type': 'application/json' }),
          ...config.headers,
        },
      });

      return result;
    } catch (err) {
      if (isAxiosError(err)) {
        const originalError =
          err.response && err.response.data ? err.response.data.error : { message: err.message };

        throw new ApiError({
          originalError,
          originalStatusCode: err.response ? err.response.status : StatusCode.BAD_REQUEST,
        });
      }

      throw err;
    }
  }

  async setHeaders(headers: RawAxiosRequestHeaders) {
    const client = this.getClient();

    Object.entries(headers).forEach(([key, value]) => {
      client.defaults.headers.common[key] = value;
    });
  }

  getHeaders(): ResponseHeaders {
    const commonHeaders = this.client?.defaults.headers.common ?? {};
    const entries = Object.entries(commonHeaders)
      .filter(([, value]) => !!value)
      .map(([key, value]): [string, string] => [key, `${value}`]);
    const headers = Object.fromEntries(entries);

    return headers;
  }
}
